;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import type { LinkField, RichTextField } from "../../../lib/content/legacyBlockTypes"
import { ContentLink } from "../../content/contentRenderPrimitives"
import GroupCard from "../homePageGroupSearch/GroupCard"
import GroupCardsRow from "../homePageGroupSearch/GroupCardsRow"
import renderGroupCards from "../homePageGroupSearch/GroupCard/renderGroupCards"
import LandingRichText from "./LandingRichText"
import {
  LandingCountLabel,
  LandingHeading,
  LandingHeadingBlock,
  LandingPanel,
  LandingSection,
  landingPrimaryButtonClass,
} from "./LandingStylePrimitives"
import type {
  LandingGroupLayout,
  PublicLandingGroup,
} from "../../../lib/landingPages/types"
import { cn } from "../../../../lib/utils"

type LandingGroupSectionProps = {
  heading: string
  intro?: RichTextField | null
  groups: PublicLandingGroup[]
  layout?: LandingGroupLayout | null
  countLabel?: string | null
  emptyHeading?: string | null
  emptyBody?: RichTextField | null
  emptyCtaLabel?: string | null
  emptyCtaLink?: LinkField | null
}

function hasLink(link?: LinkField | null) {
  return Boolean(link && "link_type" in link && link.link_type !== "Any")
}

export function LandingEmptyState({
  heading,
  body,
  ctaLabel,
  ctaLink,
}: {
  heading?: string | null
  body?: RichTextField | null
  ctaLabel?: string | null
  ctaLink?: LinkField | null
}) {
  return (
    <LandingPanel
      tone="soft"
      className="mx-auto flex max-w-[760px] flex-col items-center px-6 py-8 text-center"
    >
      <LandingHeading as="h3" size="compact">
        {heading || "No matching groups are listed yet"}
      </LandingHeading>
      <LandingRichText field={body} className="mt-4 max-w-[620px]" />
      {ctaLabel && hasLink(ctaLink) ? (
        <ContentLink
          field={ctaLink}
          className={cn("mt-5", landingPrimaryButtonClass)}
        >
          {ctaLabel}
        </ContentLink>
      ) : null}
    </LandingPanel>
  )
}

export function LandingGroupGrid({
  groups,
  layout,
  geographicListing = false,
}: {
  groups: PublicLandingGroup[]
  layout?: LandingGroupLayout | null
  geographicListing?: boolean
}) {
  return (
    <div
      className={cn(
        "mt-7 grid justify-center gap-6 [&_a]:max-w-[calc(100vw-20px)] [&_img]:h-auto [&_img]:max-w-full",
        layout === "compact"
          ? "min-[820px]:grid-cols-2"
          : "min-[760px]:grid-cols-2 min-[1120px]:grid-cols-3",
      )}
    >
      {groups.map((group, index) => (
        <GroupCard
          key={`${group.groupId}-${index}`}
          index={index}
          city={group.city}
          cohortLocations={group.cohortLocations}
          cohortNextStartsAt={group.cohortNextStartsAt}
          cohortId={group.cohortId}
          groupDescription={group.groupDescription}
          groupId={group.groupId}
          groupPublicId={group.groupPublicId}
          groupVanitySlug={group.groupVanitySlug}
          groupName={group.groupName}
          groupPhotoUrl={group.groupPhotoUrl}
          groupType={group.groupType}
          joinableCohortCount={group.joinableCohortCount}
          priceType={group.priceType}
          priceValue={group.priceValue}
          providerFirstName={group.providerFirstName}
          providerLastName={group.providerLastName}
          providerPhotoUrl={group.providerPhotoUrl}
          providerPreferredFirstName={group.providerPreferredFirstName}
          providerPreferredLastName={group.providerPreferredLastName}
          providerPrimaryCredential={group.providerPrimaryCredential}
          providerPrimaryRole={group.providerPrimaryRole}
          providerTitle={group.providerTitle}
          settingInPerson={group.settingInPerson}
          settingVirtual={group.settingVirtual}
          state={group.state}
          priority={index < 3}
          geographicListing={geographicListing}
        />
      ))}
    </div>
  )
}

export default function LandingGroupSection({
  heading,
  intro,
  groups,
  layout = "carousel",
  countLabel,
  emptyHeading,
  emptyBody,
  emptyCtaLabel,
  emptyCtaLink,
}: LandingGroupSectionProps) {
  if (groups.length === 0) {
    return (
      <LandingSection spacing="compact">
        <LandingEmptyState
          heading={emptyHeading}
          body={emptyBody}
          ctaLabel={emptyCtaLabel}
          ctaLink={emptyCtaLink}
        />
      </LandingSection>
    )
  }

  if (layout === "carousel") {
    return (
      <LandingSection spacing="compact" className="overflow-hidden">
        <LandingRichText field={intro} className="mb-3 max-w-[820px]" />
        <LandingCountLabel>{countLabel}</LandingCountLabel>
        <GroupCardsRow
          header={heading}
          cards={renderGroupCards(groups, { isFirstRow: true })}
        />
      </LandingSection>
    )
  }

  return (
    <LandingSection>
      <LandingHeadingBlock heading={heading} body={intro}>
        <LandingCountLabel className="mt-4">{countLabel}</LandingCountLabel>
      </LandingHeadingBlock>
      <LandingGroupGrid groups={groups} layout={layout} />
    </LandingSection>
  )
}
